/* eslint-disable indent */
import { Button, FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, Stack, Typography, Alert, Link } from "@mui/material"
import StepperDUC from "./StepperDUC"
import ManuscriptPrepubReviewDialog from "./ManuscriptPrepubReviewDialog"
import { Controller, useForm } from "react-hook-form"
import { LassoFormField } from "../../components/form/LassoFormField"
import { useEffect, useMemo, useRef, useState } from "react"

interface FormValues {
    previousAccessPeriod?: boolean,
    disclosureIdentified?: boolean,
    doi: string,
    pubMed: string,
    progressReportStatement: string,
    manuscriptPrepubReviewSubmitted?: boolean
}

const LOCAL_STORAGE_KEY = 'duc_progressReportStatement'

const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number) => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(() => fn(...args), delay)
    }
}

const Renewal = (props: any) => {
    const [showNoPubAlert, setShowNoPubAlert] = useState(false)

    // Reliable way to know the DUC type in this step (see SubRecipients.tsx for why this
    // isn't threaded through as a "type"/"ducType" prop instead).
    const ducType = props?.DUCInfo?.ducType ?? props?.prevData?.data?.ducType

    const initialManuscriptAnswer =
        typeof props?.DUCInfo?.manuscriptPrepubReviewSubmitted === 'boolean' ? props.DUCInfo.manuscriptPrepubReviewSubmitted
            : (typeof props?.prevData?.data?.manuscriptPrepubReviewSubmitted === 'boolean' ? props.prevData.data.manuscriptPrepubReviewSubmitted : undefined)

    // Only AI/AN renewals are asked this question, and only once - an already-recorded
    // answer (e.g. navigating back into this step) should not re-prompt.
    const [showManuscriptDialog, setShowManuscriptDialog] = useState(
        ducType === 'AIAN' && typeof initialManuscriptAnswer !== 'boolean'
    )

    const savedFromStorage = useMemo(() => {
        try {
            return localStorage.getItem(LOCAL_STORAGE_KEY) || ""
        } catch {
            return ""
        }
    }, [])

    const [progressWordCount, setProgressWordCount] = useState(0)
    const [progressOverLimit, setProgressOverLimit] = useState(false)

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            // Booleans: prefer in-flow DUCInfo → details payload; else map legacy Yes/No → boolean
            previousAccessPeriod:
                    typeof props?.DUCInfo?.previousAccessPeriod === 'boolean' ? props.DUCInfo.previousAccessPeriod
                        : (typeof props?.prevData?.data?.previousAccessPeriod === 'boolean' ? props.prevData.data.previousAccessPeriod
                            : (props?.DUCInfo?.publication ? props.DUCInfo.publication === 'Yes'
                                : (props?.prevData?.data?.publication ? props.prevData.data.publication === 'Yes' : undefined))),
            disclosureIdentified:
                    typeof props?.DUCInfo?.disclosureIdentified === 'boolean' ? props.DUCInfo.disclosureIdentified
                        : (typeof props?.prevData?.data?.disclosureIdentified === 'boolean' ? props.prevData.data.disclosureIdentified
                            : (props?.DUCInfo?.identified ? props.DUCInfo.identified === 'Yes'
                                : (props?.prevData?.data?.identified ? props.prevData.data.identified === 'Yes' : undefined))),
            manuscriptPrepubReviewSubmitted: initialManuscriptAnswer,

            // Text fields: prefer new keys → details new keys → legacy keys → details legacy → empty
            doi: props?.DUCInfo?.doi
                ?? props?.prevData?.data?.doi
                ?? props?.DUCInfo?.DOI
                ?? props?.prevData?.data?.DOI
                ?? "",
            pubMed: props?.DUCInfo?.pubMed
                ?? props?.prevData?.data?.pubMed
                ?? props?.DUCInfo?.pubmed
                ?? props?.prevData?.data?.pubmed
                ?? "",

            // Statement: DUCInfo → details → local backup → empty
            progressReportStatement:
                props?.DUCInfo?.progressReportStatement
                ?? props?.prevData?.data?.progressReportStatement
                ?? savedFromStorage
                ?? ""
        },
    });

    const handleManuscriptAnswer = (submitted: boolean) => {
        setValue('manuscriptPrepubReviewSubmitted', submitted)
        props.setDUCInfo({ ...props.DUCInfo, manuscriptPrepubReviewSubmitted: submitted })
        setShowManuscriptDialog(false)
    }

    const previousAccessPeriod = watch('previousAccessPeriod')
    const disclosureIdentified = watch('disclosureIdentified')

    // Keep the publications alert in sync with the two radio answers
    useEffect(() => {
        setShowNoPubAlert(previousAccessPeriod === true && disclosureIdentified === false)
    }, [previousAccessPeriod, disclosureIdentified])

    // Persist progressReportStatement as user types to prevent data loss on refresh/navigation
    const prs = watch('progressReportStatement')
    const saveToLocalStorage = useRef(
        debounce((value: string) => {
            localStorage.setItem(LOCAL_STORAGE_KEY, value)
        }, 300)
    )
    useEffect(() => {
        saveToLocalStorage.current(prs || "")
    }, [prs])

    // Track live word count and over-limit state for immediate UX feedback
    useEffect(() => {
        const wc = (prs || "").trim().split(/\s+/).filter(Boolean).length
        setProgressWordCount(wc)
        setProgressOverLimit(wc > 250)
    }, [prs])

    // No-op: previousAccessPeriod is handled directly via watch() and boolean mapping

    const handleClick = (newData: FormValues) => {
        // AI/AN renewals must have an on-record answer to the pre-publication review question
        // before continuing - re-open the dialog if it was somehow dismissed unanswered.
        if (ducType === 'AIAN' && typeof newData.manuscriptPrepubReviewSubmitted !== 'boolean') {
            setShowManuscriptDialog(true)
            return
        }
        // If user answered Yes to having a publication but No to reporting it on the Data Hub,
        // block progression and show the publications alert with link.
        if (newData.previousAccessPeriod === true && newData.disclosureIdentified === false) {
            setShowNoPubAlert(true)
            return
        }
        // Merge new boolean fields and remove legacy string fields if present
        const updated = { ...props.DUCInfo, ...newData } as any
        delete updated.publication
        delete updated.identified
        delete updated.DOI
        delete updated.pubmed
        props.setDUCInfo(updated)
        // Clear local backup on successful continue
        try { localStorage.removeItem(LOCAL_STORAGE_KEY) } catch { /* ignore storage errors */ }
        props.setCurStep(props.curStep + 1)
    }

    const handleClickBack = () => {
        props.setCurStep(props.curStep - 1)
    }

    const validateWordCount = (value: string, maxWords: number, fieldName: string) => {
        if (!value || value.trim() === "") return `${fieldName} is required`;
        const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
        return wordCount <= maxWords || `${fieldName} cannot exceed ${maxWords} words`;
    };
    
    return (
        <>
            <ManuscriptPrepubReviewDialog open={showManuscriptDialog} onAnswer={handleManuscriptAnswer} />
            <Stack gap={3}>
                <Stack flexDirection="column" gap={4}>
                    <StepperDUC
                        activeStep={props.curStep}
                        stepLabels={props.stepLabels} />
                </Stack>

                <Typography variant="h6" fontWeight="600">
                        Renewal
                </Typography>

                <Typography>
                Researchers who conduct secondary analyses on shared data are expected as part of the Terms of Use to report their results (See #13 in the general DUC).
                </Typography>

                <Stack flexDirection="column" gap={2}>
                    <Typography fontWeight={600}>Progress Report Statement</Typography>
                    <Typography variant="body2">
                        Recipients requesting a renewal of an expiring Data Use Certification should provide a Progress Report on research conducted with data from the NIH Brain Development Cohorts Data Hub. The Progress Report should also describe 1) any updates to the original Research Data Use Statement; 2) any violations of the terms of access (e.g., data misuse, breaches, security incidents and the implemented remediation; and 3) information on any downstream intellectual property generated from the data.
                        If a publication resulted from the previous access period, i) please indicate whether you shared derived data back to NBDC (e.g., community collection), and ii) whether you made any codes/algorithms available (e.g., GitHub).
                    </Typography>
                    <Typography variant="caption">
                        Please note the progress report statement cannot exceed 250 words
                    </Typography>
                    <LassoFormField
                        label = ""
                        placeholder="Describe the progress of your research..."
                        controller={{
                            name: "progressReportStatement",
                            control: control,
                            rules: {
                                validate: (v: string) => validateWordCount(v, 250, "Progress report statement")
                            }
                        }}
                        fullWidth
                        multiline
                        minRows={8}
                        marginTop={2}
                        sx={{
                            '& .MuiInputBase-root textarea': {
                                maxHeight: '60vh',
                                overflow: 'auto'
                            }
                        }}
                        error={progressOverLimit || !!errors.progressReportStatement}
                        helperText={progressOverLimit ? "Progress report statement cannot exceed 250 words" : (errors.progressReportStatement?.message || "")}
                        onChange={(_e: any, field: any) => {
                            field.onChange(_e)
                            // mirror into DUCInfo so it persists when navigating between steps
                            props.setDUCInfo({ ...props.DUCInfo, progressReportStatement: _e.target?.value ?? _e })
                        }}
                    />
                    <Typography sx={{ textAlign: "right" }}>
                        Current word count: <span id="duc-renewal-progress-wordcount">{progressWordCount}</span>
                    </Typography>
                </Stack>

                <Stack
                    flexDirection="column">
                    <Typography>
                        Has a publication, computational pipeline, or other public disclosure of results from the analysis of data accessed from 
                        NIH Brain Development Cohorts resulted from a Recipient’s previous access period? Note 
                        that publication includes preprint services (e.g., PsyArXiv, BioRxiv).
                    </Typography>

                    <FormControl error={!!errors.previousAccessPeriod}>
                        <Controller                            
                            rules={{ validate: (v) => (typeof v === 'boolean') || 'This is a required field' }}
                            control={control}
                            name="previousAccessPeriod"
                            render={({ field }) =>(
                                <RadioGroup
                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                    name="controlled-radio-buttons-group"
                                    value={field.value === true ? 'Yes' : field.value === false ? 'No' : ''}
                                    onChange={(e) => {
                                        const yes = e.target.value === 'Yes'
                                        field.onChange(yes)
                                        // Clear the dependent answer when switching to No
                                        if (!yes) {
                                            setValue('disclosureIdentified', false)
                                            setValue('doi', "")
                                            setValue('pubMed', "")
                                        }
                                    }}>
                                    <Stack flexDirection="row">
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                        <FormControlLabel value="No" control={<Radio />} label="No" />
                                    </Stack>
                                </RadioGroup>)}/>
                        {errors.previousAccessPeriod && <FormHelperText>{errors.previousAccessPeriod.message}</FormHelperText>}
                    </FormControl>
                </Stack>

                <Stack
                    flexDirection="column">
                    <Typography>
                        If Yes, has the publication been reported on the NBDC Data Hub?
                    </Typography>

                    <FormControl error={!!errors.disclosureIdentified && previousAccessPeriod === true}>
                        <Controller                            
                            control={control}
                            name="disclosureIdentified"
                            rules={{
                                validate: (v) => {
                                    if (previousAccessPeriod === true) {
                                        return typeof v === 'boolean' ? true : 'This is a required field'
                                    }
                                    return true
                                }
                            }}
                            render={({ field }) =>(
                                <RadioGroup
                                    aria-labelledby="demo-controlled-radio-buttons-group"
                                    name="controlled-radio-buttons-group"
                                    value={field.value === true ? 'Yes' : field.value === false ? 'No' : ''}
                                    onChange={(e) => field.onChange(e.target.value === 'Yes')}
                                >
                                    <Stack flexDirection="row">
                                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" disabled={previousAccessPeriod !== true}/>
                                        <FormControlLabel value="No" control={<Radio />} label="No" disabled={previousAccessPeriod !== true}/>
                                    </Stack>
                                </RadioGroup>)}/>
                        {errors.disclosureIdentified && previousAccessPeriod === true && (
                            <FormHelperText>{(errors as any).disclosureIdentified?.message || 'This is a required field'}</FormHelperText>
                        )}
                    </FormControl>
                </Stack>

                <Stack
                    flexDirection="column"
                    gap={3}>

                    {showNoPubAlert && (
                        <Alert severity="warning">
                            Recipients agree to register any publications resulting from data accessed in the NIH Brain Development Cohorts Data Hub once a DOI and/or PubMed ID is assigned to a publication using these data. {" "}
                            <Link href="/publications" underline="hover">Go to the NBDC publications page</Link>.
                        </Alert>
                    )}

                    <Stack gap={5}> 

                    <LassoFormField
                        label = "List the DOI(s)" 
                        controller={{
                            name: "doi",
                            control: control,
                            rules: {
                                validate: (v: any) => {
                                    if (previousAccessPeriod === true && disclosureIdentified === true) {
                                        if (!v || v.trim() === "") return "The DOI(s) are required"
                                    }
                                    return true
                                }
                            }

                        }}
                        fullWidth
                        disabled={!(previousAccessPeriod === true && disclosureIdentified === true)}
                        multiline
                        minRows={3}
                        sx={{
                            '& .MuiInputBase-root textarea': {
                                maxHeight: '50vh',
                                overflow: 'auto'
                            }
                        }}
                        error={errors.doi}
                        marginTop ={10}
                        helperText={errors?.doi?.message || ""}
                    />

                    <LassoFormField
                        label= "List the PubMed ID(s) or citation(s)"
                        controller={{
                            name: "pubMed",
                            control: control,
                            rules: {
                                validate: (v: any) => {
                                    if (previousAccessPeriod === true && disclosureIdentified === true) {
                                        if (!v || v.trim() === "") return "The PubMed ID(s) or citation(s) are required"
                                    }
                                    return true
                                }
                            }
                        }}
                        fullWidth
                        disabled={!(previousAccessPeriod === true && disclosureIdentified === true)}
                        multiline
                        minRows={3}
                        sx={{
                            '& .MuiInputBase-root textarea': {
                                maxHeight: '50vh',
                                overflow: 'auto'
                            }
                        }}
                        marginTop ={10}
                        error={errors.pubMed}
                        helperText={errors?.pubMed?.message || ""}
                    />
                </Stack>

                {/* Close the column stack that contains the alert and fields */}
                </Stack>
                {/* eslint-enable indent */}

                <Stack
                    flexDirection="row"
                    justifyContent="space-between">

                    <Button
                        variant="tertiary"
                        onClick={handleClickBack}
                        sx={{ alignSelf: "center" }}
                    >
                        Back
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleSubmit(handleClick)}
                        sx={{ alignSelf: "center", marginTop: 3 }}
                    >
                        Continue
                    </Button>

                </Stack>

            </Stack>
        </>
    )
}

export default Renewal